import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { doDelete, get, patch, post } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import InquiryFormWidgetEditor from '../../../helpers/components/InquiryFormWidgetEditor';
import BackButtonWidgetEditor from '../../../helpers/components/BackButtonWidgetEditor';

const WIDGET_TYPES = ['inquiry_form', 'back_button'];

const DEFAULT_CONFIG = {
  inquiry_form: {
    fields: [],
    title: '',
    description: '',
    submitLabel: 'Send',
    successMessage: '',
    transportId: null,
  },
  back_button: {
    title: 'Back',
  },
};

function WidgetForm({ websiteId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState(initial?.type ?? WIDGET_TYPES[0]);
  const [config, setConfig] = useState(() => initial?.config ?? { ...DEFAULT_CONFIG[initial?.type ?? WIDGET_TYPES[0]] });
  const [saving, setSaving] = useState(false);

  const handleTypeChange = (e) => {
    const t = e.target.value;
    setType(t);
    setConfig({ ...DEFAULT_CONFIG[t] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      setSaving(true);
      const payload = isEdit
        ? { name: name.trim(), config }
        : { name: name.trim(), type, config };
      const result = isEdit
        ? await patch(`websites/${websiteId}/widgets/${initial.id}`, payload)
        : await post(`websites/${websiteId}/widgets`, payload);
      onSaved(result.widget);
    } catch {
      /* httpClient shows toast */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="mb-3">
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Contact form"
        />
      </Form.Group>

      {!isEdit && (
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select value={type} onChange={handleTypeChange}>
            {WIDGET_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      <div className="mb-3">
        {(isEdit ? initial.type : type) === 'inquiry_form' && (
          <InquiryFormWidgetEditor item={config} websiteId={websiteId} onChange={setConfig} />
        )}
        {(isEdit ? initial.type : type) === 'back_button' && (
          <BackButtonWidgetEditor item={config} websiteId={websiteId} onChange={setConfig} />
        )}
      </div>

      <div className="form-actions mt-3">
        <Button type="submit" className="primary-button" disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : 'Save'}
        </Button>
        <Button type="button" className="primary-button" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}

function Widgets({ website }) {
  const websiteId = website.id;

  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await get(`websites/${websiteId}/widgets`);
      setWidgets(resp?.widgets ?? []);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (widget) => {
    setWidgets(prev => [...prev, widget]);
    setCreating(false);
  };

  const handleUpdated = (widget) => {
    setWidgets(prev => prev.map(c => (c.id === widget.id ? widget : c)));
    setEditing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this widget? It will be removed from all pages.')) return;
    try {
      setDeletingId(id);
      await doDelete(`websites/${websiteId}/widgets/${id}`);
      setWidgets(prev => prev.filter(c => c.id !== id));
    } catch {
      /* httpClient shows toast */
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  if (editing) {
    return (
      <div>
        <div className="structure-header-row mb-3">
          <h5 className="mb-0">Edit widget</h5>
        </div>
        <WidgetForm
          websiteId={websiteId}
          initial={editing}
          onSaved={handleUpdated}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="structure-header-row mb-3">
        <h5 className="mb-0">Widgets</h5>
        {!creating && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button size="sm" type="button" className="primary-button m-0" onClick={() => setCreating(true)}>
              New widget
            </Button>
          </div>
        )}
      </div>

      {creating && (
        <WidgetForm
          websiteId={websiteId}
          onSaved={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {!creating && widgets.length === 0 && (
        <div className="gl-empty">No widgets created yet.</div>
      )}

      {!creating && widgets.map(c => (
        <div key={c.id} className="gl-card">
          <div className="gl-card-info">
            <div className="gl-card-name">{c.name}</div>
            <div className="gl-card-meta">
              <span className="gl-meta-pill">{c.type}</span>
            </div>
          </div>
          <div className="gl-card-actions">
            <Button size="sm" type="button" className="primary-button m-0" onClick={() => setEditing(c)}>
              Edit
            </Button>
            <button
              type="button"
              className="gl-delete-btn"
              onClick={() => handleDelete(c.id)}
              disabled={deletingId === c.id}
            >
              {deletingId === c.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default Widgets;
